import React, { SyntheticEvent } from "react";
import classnames from "classnames";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { addFollower, FolloweeType } from "api/requests/addFollower";
import { getIsFollowing } from "api/requests/getIsFollowing";
import { removeFollower } from "api/requests/removeFollower";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useSsoUrls } from "hooks/useSsoUrls";
import { getIsLoggedIn } from "redux/selectors/user";
import logError from "utils/logError";

import HeartFilledIcon from "components/Icons/heart_filled.svg";
import HeartOutlineIcon from "components/Icons/heart_outline.svg";

type Props = {
  followeeType: FolloweeType;
  followeeId: number;
  className?: string;
  onFollow?: () => void;
  onUnfollow?: () => void;
};

const FollowingButton = ({
  followeeType,
  followeeId,
  className,
  onFollow = () => {},
  onUnfollow = () => {},
}: Props) => {
  const [isFollowing, setIsFollowing] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const countryCode = useDomainCountryCode();
  const isLoggedIn = useSelector(getIsLoggedIn);

  const router = useRouter();

  React.useEffect(() => {
    if (!router?.isReady) {
      return;
    }

    // If there are URL params for following, then the user was not logged in
    // and clicked on a favorite heart, then sent to signup/signin, then redirected.
    // Now add the favorite.
    const { followButton, fType, fId } = router.query;
    if (followButton && fType === followeeType && fId) {
      try {
        if (typeof fId == "string" && parseInt(fId) === followeeId) {
          addFollow();

          // Clean out the query params.  Don't want to re-follow
          // if someone shares or bookmarks the link.
          const params = router.query;
          delete params.fType;
          delete params.fId;
          delete params.followButton;
          router.replace(router);
        }
      } catch {
        logError("Error adding follow in FollowingButton useEffect");
      }
    } else {
      getIsFollowing({ countryCode, followeeId, followeeType }).then(
        (result: boolean) => {
          setIsFollowing(result);
        }
      );
    }
  }, [router?.isReady]);

  const { signUpUrl } = useSsoUrls({
    signUp: {
      redirectUrlParams: {
        fId: `${followeeId}`,
        fType: followeeType,
        followButton: "1",
      },
    },
  });

  const addFollow = async () => {
    setIsUpdating(true);

    const addFollowerSuccess = await addFollower({
      countryCode,
      followeeId,
      followeeType,
    });

    if (addFollowerSuccess) {
      setIsFollowing(true);
      onFollow();
    }

    setIsUpdating(false);
  };

  const removeFollow = async () => {
    setIsUpdating(true);

    const removeFollowerSuccess = await removeFollower({
      countryCode,
      followeeId,
      followeeType,
    });

    if (removeFollowerSuccess) {
      setIsFollowing(false);
      onUnfollow();
    }

    setIsUpdating(false);
  };

  const toggleFollows = (ev: SyntheticEvent) => {
    ev.preventDefault();

    if (!isLoggedIn) {
      router.push(signUpUrl);
    } else if (isFollowing) {
      removeFollow();
    } else {
      addFollow();
    }
  };

  return (
    <button
      aria-pressed={isFollowing}
      onClick={toggleFollows}
      className={classnames(className)}
      data-testid="followingButton"
      disabled={isUpdating}
      aria-label={isFollowing ? "Click to follow" : "Click to unfollow"}
    >
      {isFollowing ? (
        <HeartFilledIcon height="24" width="24" data-testid="heart-filled" />
      ) : (
        <HeartOutlineIcon height="24" width="24" data-testid="heart-outline" />
      )}
    </button>
  );
};

export default FollowingButton;
